import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUserProfile } from '@busy/api';
import { useT } from '../../lib/i18n';

// Sunday banner that nudges the barber to set / review this week's
// target hours. Surfaced starting every Sunday and persists every day
// of that week until the barber taps Done — at which point it hides
// until the upcoming Sunday rolls in.
//
// Tapping the body (anywhere outside the Done button) deep-links to
// Settings → Profile → Target hours per week. The settings page reads
// the `?focus=targetHours` param and scrolls the field into view.
//
// Honors users.weekly_target_reminder_enabled — when false, never
// renders. (Settings has the toggle that flips that flag.)

const DISMISS_STORAGE_KEY = 'busy.weeklyTargetReminder.lastDoneYmd';

/** YYYY-MM-DD of the most recent Sunday (today, if today is Sunday).
 *  Used to anchor the once-per-week visibility window. */
function thisWeekSundayYmd(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay()); // getDay(): Sun=0
  return ymd(d);
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function WeeklyTargetReminder() {
  const profile = useCurrentUserProfile();
  const t = useT();
  const navigate = useNavigate();

  // Track the last Sunday the user marked Done. The reminder hides
  // for the rest of the week and reappears next Sunday.
  const [doneYmd, setDoneYmd] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(DISMISS_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const reminderEnabled = profile.data?.weekly_target_reminder_enabled ?? true;
  const target = profile.data?.target_hours_per_week ?? null;
  // Days defaults to 5 when missing — matches the migration backfill
  // and the UI default everywhere else. The reminder copy shows the
  // pair so the barber's mental model of "X hours across Y days"
  // stays consistent across surfaces.
  const targetDays = profile.data?.target_days_per_week ?? 5;
  const thisSunday = thisWeekSundayYmd();

  // Visibility gates. If any fail, the reminder is silent.
  if (!profile.data) return null;
  if (!reminderEnabled) return null;
  // doneYmd === thisSunday means the user dismissed it this week already.
  // Anything earlier (or null) means the new week has rolled in.
  if (doneYmd === thisSunday) return null;

  function markDone(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, thisSunday);
    } catch {
      /* ignore — quota / private mode; reminder will reappear on next render */
    }
    setDoneYmd(thisSunday);
  }

  function openTarget() {
    // Deep-link straight to the Target hours per week field. Settings.tsx
    // reads ?focus=targetHours and scrolls the field into view.
    navigate('/settings?section=profile&focus=targetHours');
  }

  return (
    <button
      type="button"
      onClick={openTarget}
      className="w-full text-left rounded-xl border border-line bg-bg-alt px-3 py-2.5 flex items-start gap-2.5 hover:border-navy-900/40 dark:hover:border-gold-500/40 transition-colors"
    >
      <ClockIcon className="w-4 h-4 text-ink-500 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] leading-snug text-ink-900 dark:text-ink-100">
          {target == null
            ? t('weeklyReminder.unsetTitle')
            : t('weeklyReminder.body', { hours: target, days: targetDays })}
        </p>
        <span className="block text-[11px] text-ink-500 mt-0.5">
          {t('weeklyReminder.tapToOpen')}
        </span>
      </div>
      <span
        role="button"
        tabIndex={0}
        onClick={markDone}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            markDone(e as unknown as React.MouseEvent);
          }
        }}
        className="shrink-0 h-8 px-3 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 text-[12px] font-bold inline-flex items-center"
      >
        {t('weeklyReminder.done')}
      </span>
    </button>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
